<template>
  <section class="pt-20 lg:pt-25 pb-15" v-if="items.length">
    <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
      <!-- Section header -->
      <div
        class="flex flex-col gap-4 mb-10 sm:flex-row sm:items-end sm:justify-between"
      >
        <div class="max-w-2xl">
          <span
            class="inline-flex items-center gap-2 px-3 py-1 mb-3 text-xs font-semibold tracking-wide uppercase rounded-full text-primary bg-primary/10"
          >
            <span class="inline-block w-1.5 h-1.5 rounded-full bg-primary"></span>
            Informasi
          </span>
          <h2
            class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl font-roboto-slab"
          >
            Berita &amp; Informasi Terkini
          </h2>
          <p class="mt-3 text-base leading-relaxed text-gray-500">
            Kabar terbaru, pengumuman, dan kegiatan terkini.
          </p>
        </div>

        <Link
          href="/informasi"
          class="inline-flex items-center self-start gap-2 px-4 py-2 text-sm font-semibold transition-colors duration-200 border rounded-lg shrink-0 text-primary border-primary/30 hover:bg-primary hover:text-white hover:border-primary group"
        >
          Lihat semua
          <i
            class="text-xs transition-transform fa-solid fa-arrow-right group-hover:translate-x-1"
          ></i>
        </Link>
      </div>

      <!-- Grid -->
      <div class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        <article
          v-for="item in items"
          :key="item.id"
          class="flex flex-col overflow-hidden transition-all duration-300 bg-white border border-gray-100 shadow-sm group rounded-2xl hover:shadow-lg hover:-translate-y-1"
        >
          <Link
            :href="`/read/${item.slug}`"
            class="relative block overflow-hidden aspect-[16/10] bg-gray-100"
          >
            <img
              v-if="firstImage(item.image)"
              :src="firstImage(item.image)"
              :alt="item.title"
              class="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
            />
            <div
              v-else
              class="flex items-center justify-center w-full h-full text-gray-300"
            >
              <i class="text-4xl fa-regular fa-image"></i>
            </div>
            <span
              v-if="item.tags && item.tags.length"
              class="absolute px-2.5 py-1 text-xs font-semibold text-white rounded-full top-3 left-3 bg-primary/90 backdrop-blur-sm"
            >
              {{ item.tags[0] }}
            </span>
          </Link>

          <div class="flex flex-col flex-1 p-5">
            <div
              class="flex flex-wrap items-center gap-2 mb-2 text-xs text-gray-400"
            >
              <span class="inline-flex items-center gap-1">
                <i class="fa-regular fa-user"></i>
                {{ item.author }}
              </span>
              <span class="w-1 h-1 rounded-full bg-gray-300"></span>
              <span class="inline-flex items-center gap-1">
                <i class="fa-regular fa-calendar"></i>
                {{ item.publish_date }}
              </span>
              <span class="w-1 h-1 rounded-full bg-gray-300"></span>
              <span class="inline-flex items-center gap-1" title="Jumlah dibaca">
                <i class="fa-regular fa-eye"></i>
                {{ item.formatted_view_count ?? item.view_count ?? 0 }}
              </span>
            </div>

            <h3
              class="text-lg font-bold leading-snug text-gray-900 transition-colors line-clamp-2 group-hover:text-primary font-roboto-slab"
            >
              <Link :href="`/read/${item.slug}`">{{ item.title }}</Link>
            </h3>

            <p class="flex-1 mt-2 text-sm text-gray-500 line-clamp-2">
              {{ excerpt(item.content) }}
            </p>

            <div class="flex items-center justify-between pt-4 mt-4 border-t border-gray-100">
              <Link
                :href="`/read/${item.slug}`"
                class="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all"
              >
                Baca selengkapnya
                <i class="text-xs fa-solid fa-arrow-right"></i>
              </Link>

              <!-- Bagikan -->
              <div class="flex items-center gap-2 shrink-0">
                <a
                  :href="facebookShareUrl(item)"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  class="flex items-center justify-center text-white transition-colors bg-blue-600 rounded-full w-7 h-7 hover:bg-blue-700"
                  aria-label="Bagikan ke Facebook"
                  @click.stop
                >
                  <i class="text-xs fa-brands fa-facebook-f"></i>
                </a>
                <a
                  :href="whatsappShareUrl(item)"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  class="flex items-center justify-center text-white transition-colors bg-green-500 rounded-full w-7 h-7 hover:bg-green-600"
                  aria-label="Bagikan ke WhatsApp"
                  @click.stop
                >
                  <i class="text-xs fa-brands fa-whatsapp"></i>
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { Link } from "@inertiajs/vue3";

const props = defineProps({
  news: {
    type: Object,
    required: true,
  },
});

const items = computed(() => props.news?.data || []);

// Share via the server-rendered /share/{slug} page (has OG meta for rich previews).
const shareUrl = (item) =>
  `${typeof window !== "undefined" ? window.location.origin : ""}/share/${item.slug}`;

const facebookShareUrl = (item) =>
  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl(item))}`;

const whatsappShareUrl = (item) =>
  `https://wa.me/?text=${encodeURIComponent(`${item.title} ${shareUrl(item)}`)}`;

// `image` is cast to an array on the model; return the first usable URL.
const firstImage = (image) => {
  if (Array.isArray(image)) return image[0] || null;
  if (typeof image === "string" && image) return image;
  return null;
};

// Strip HTML tags and collapse whitespace to produce a clean text excerpt.
const excerpt = (html, max = 120) => {
  const text = (html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? text.slice(0, max).trim() + "…" : text;
};
</script>
